import { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Skeleton } from "../ui/skeleton";
import { cn } from "@/lib/utils/ui";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc } from "@/lib/trpc/client";
import {
  DistanceUnit,
  formatDistance,
  formatTimeSeconds,
} from "@/lib/utils/exerciseTracking";
import { Exercise, TrackingType } from "./workoutsColumns";

type ExerciseHistorySet = {
  set_num: number | null;
  reps: number | null;
  weight: number | null;
  time_seconds: number | null;
  distance_meters: number | null;
  is_pr: boolean | null;
  start_time: string | null;
  workout_name: string | null;
  workout_log_id: string | null;
};

type ExerciseHistoryEntry = {
  workoutLogId: string;
  workoutName: string | null;
  startTime: string | null;
  sets: ExerciseHistorySet[];
};

type ExerciseHistoryPopoverProps = {
  exercise: Pick<Exercise, "id" | "name" | "tracking_type">;
  clientId?: string;
  readOnly?: boolean;
  weightUnitLabel?: string;
  distanceUnitLabel?: DistanceUnit;
  onExerciseSelected: () => void;
};

const formatHistoryDate = (value: string | null) => {
  if (!value) return "Previous workout";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Previous workout";
  return new Intl.DateTimeFormat(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
  }).format(date);
};

const ExerciseHistoryPopover = ({
  exercise,
  clientId,
  readOnly = false,
  weightUnitLabel,
  distanceUnitLabel,
  onExerciseSelected,
}: ExerciseHistoryPopoverProps) => {
  const [open, setOpen] = useState(false);
  const org = useOrg();
  const distanceUnit: DistanceUnit =
    distanceUnitLabel ?? (org?.distanceUnitDefault === "mi" ? "mi" : "km");
  const trackingType = (exercise.tracking_type ?? "strength") as TrackingType;

  const { data: exerciseHistoryData, isLoading } =
    trpc.workouts.exerciseHistory.useQuery(
      { clientId: clientId ?? "", exerciseId: exercise.id, limit: 18 },
      { enabled: Boolean(clientId && open) },
    );
  const exerciseHistory = exerciseHistoryData?.sets;

  const historyEntries = useMemo(() => {
    const entries = new Map<string, ExerciseHistoryEntry>();

    (exerciseHistory ?? []).forEach((set) => {
      const workoutLogId =
        set.workout_log_id ??
        `${set.start_time ?? "unknown"}-${set.workout_name ?? "workout"}`;
      const existing = entries.get(workoutLogId);

      if (existing) {
        existing.sets.push(set);
        return;
      }

      entries.set(workoutLogId, {
        workoutLogId,
        workoutName: set.workout_name,
        startTime: set.start_time,
        sets: [set],
      });
    });

    return Array.from(entries.values())
      .map((entry) => ({
        ...entry,
        sets: [...entry.sets].sort(
          (a, b) => (a.set_num ?? 0) - (b.set_num ?? 0),
        ),
      }))
      .slice(0, 3);
  }, [exerciseHistory]);

  const formatHistorySet = (set: ExerciseHistorySet) => {
    const parts: string[] = [];
    const hasReps = set.reps !== null && set.reps !== undefined;
    const hasWeight = set.weight !== null && set.weight !== undefined;
    const weightUnit = weightUnitLabel ?? org?.weightUnitDefault ?? "kg";

    if (hasWeight && hasReps) {
      parts.push(`${set.weight}${weightUnit} x ${set.reps}`);
    } else if (hasWeight) {
      parts.push(`${set.weight}${weightUnit}`);
    } else if (hasReps) {
      parts.push(`${set.reps} reps`);
    }

    const time = formatTimeSeconds(set.time_seconds);
    if (time) {
      parts.push(time);
    }

    const distance = formatDistance(
      set.distance_meters,
      distanceUnit,
      trackingType,
    );
    if (distance) {
      parts.push(distance);
    }

    return parts.length > 0 ? parts.join(" | ") : "Logged set";
  };

  const trigger = (
    <h2
      className={cn(
        "text-sm",
        !readOnly && "hover:text-primary hover:cursor-pointer",
      )}
      onMouseEnter={() => {
        if (clientId) setOpen(true);
      }}
      onMouseLeave={() => setOpen(false)}
      onClick={() => {
        if (readOnly) return;
        onExerciseSelected();
      }}
    >
      {exercise.name}
    </h2>
  );

  if (!clientId) {
    return trigger;
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        side="top"
        align="start"
        className="w-80 p-3"
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium">Recent logged sets</p>
            <p className="text-xs text-muted-foreground">{exercise.name}</p>
          </div>
          {isLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          ) : historyEntries.length > 0 ? (
            <div className="space-y-3">
              {historyEntries.map((entry) => (
                <div
                  key={entry.workoutLogId}
                  className="space-y-1 border-t pt-2 first:border-t-0 first:pt-0"
                >
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-xs font-medium">
                      {entry.workoutName ?? "Workout"}
                    </p>
                    <p className="shrink-0 text-xs text-muted-foreground">
                      {formatHistoryDate(entry.startTime)}
                    </p>
                  </div>
                  <div className="space-y-1">
                    {entry.sets.slice(0, 4).map((set, index) => (
                      <div
                        key={`${entry.workoutLogId}-${set.set_num ?? index}`}
                        className="flex items-center gap-2 text-xs text-muted-foreground"
                      >
                        <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-muted text-[11px] font-medium text-foreground">
                          {set.set_num !== null && set.set_num !== undefined
                            ? set.set_num + 1
                            : index + 1}
                        </span>
                        <span className="truncate">
                          {formatHistorySet(set)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No logged sets found for this client.
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default ExerciseHistoryPopover;
